<template>
    <div class="rlms-flex rlms-flex-col rlms-gap-desk30-mob-20">
        <!-- Header with buttons -->
        <div class="rlms-flex rlms-items-center rlms-justify-between rlms-flex-wrap rlms-gap-10">
            <div class="rlms-fs-lg rlms-fw-600">Administrators</div>
            <div class="rlms-flex rlms-items-center rlms-gap-5">
                <button class="rlms-btn rlms-btn-primary" @click="showModal = true;">Add New Administrator</button>
                <svg v-if="selectedUsers.length > 0" class="rlms-popup-btn del-btn" @click="deleteSelected" width="50"
                    height="46" viewBox="0 0 50 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="50" height="46" rx="12" fill="#FFE6DD" />
                    <path
                        d="M36.25 15.4756C32.0875 15.0631 27.9 14.8506 23.725 14.8506C21.25 14.8506 18.775 14.9756 16.3 15.2256L13.75 15.4756"
                        stroke="#FF5664" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path
                        d="M20.625 14.2125L20.9 12.575C21.1 11.3875 21.25 10.5 23.3625 10.5H26.6375C28.75 10.5 28.9125 11.4375 29.1 12.5875L29.375 14.2125"
                        stroke="#FF5664" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path
                        d="M33.5625 19.4258L32.75 32.0133C32.6125 33.9758 32.5 35.5008 29.0125 35.5008H20.9875C17.5 35.5008 17.3875 33.9758 17.25 32.0133L16.4375 19.4258"
                        stroke="#FF5664" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M22.9141 28.625H27.0766" stroke="#FF5664" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" />
                    <path d="M21.875 23.625H28.125" stroke="#FF5664" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" />
                </svg>
            </div>
        </div>


        <div class="rlms-table">
            <div class="rlms-table-inner">
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Join Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        <!-- Owner Row -->
                        <tr v-if="ownerData">
                            <td>
                                <label class="rlms-flex rlms-items-center rlms-gap-10 rlms-w-fc rlms-us-none">
                                    <input type="checkbox" class="rlms-checkbox" disabled style="visibility: hidden;">
                                    <div class="rlms-flex rlms-items-center rlms-gap-10 rlms-w-mc">
                                        <img :src="profileIcon">
                                        <p class="rlms-fw-500">
                                            {{ ownerData.firstname }} {{ ownerData.lastname }} <small>(Owner)</small>
                                        </p>
                                    </div>
                                </label>
                            </td>
                            <td>{{ ownerData.email }}</td>
                            <td>
                                <p class="rlms-text-extra-3">{{ formatDate(organization.created_at) }}</p>
                            </td>
                        </tr>

                        <!-- Administrator Rows -->
                        <template v-if="administrators.length > 0">
                            <tr v-for="admin in administrators" :key="admin.id">
                                <td>
                                    <label class="rlms-flex rlms-items-center rlms-gap-10 rlms-w-fc rlms-us-none">
                                        <input type="checkbox" :value="admin.id" v-model="selectedUsers"
                                            class="rlms-checkbox">
                                        <div class="rlms-flex rlms-items-center rlms-gap-10 rlms-w-mc">
                                            <img :src="profileIcon">
                                            <p class="rlms-fw-500">{{ admin.details.firstname }} {{
                                                admin.details.lastname
                                                }}</p>
                                        </div>
                                    </label>
                                </td>
                                <td>{{ admin.details.email }}</td>
                                <td>
                                    <p class="rlms-text-extra-3">{{ formatDate(admin.created_at) }}</p>
                                </td>
                            </tr>
                        </template>

                        <!-- No administrators message -->
                        <tr v-if="!administrators.length && !ownerData">
                            <td colspan="5">
                                <div class="rlms-flex rlms-flex-col rlms-gap-30 rlms-text-center rlms-p-3">
                                    <img :src="noDataImg" class="rlms-mx-auto"
                                        style="width: clamp(150px, -1533rem + 3200vw, 150px);">
                                    <p class="rlms-text-center">No record found.</p>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <paginate v-model="page" :page-count="totalPages" :margin-pages="2" :prev-text="'<'" :next-text="'>'"
            :container-class="'pagination'" :page-class="'page-item'" :active-class="'active-page'"
            v-if="totalItems > 0" />
    </div>
    <AddAdministratorModal v-if="showModal" @close="showModal = false;" @saved="init()"
        :organizationId="organizationId" />
</template>
<script setup>
import { useRoute, useRouter } from 'vue-router'
import { onMounted, ref, watch } from 'vue'
import axios from "@/services/axios";
import { formatDate } from '@/utils/format-helpers';
import { debounce } from '@/utils/debounce';
import paginate from 'vuejs-paginate-next';
import AddAdministratorModal from '@/components/AddAdministratorModal.vue';
import confirmationIcon from '@/assets/images/confirmation.png';
import { handleError } from '@/utils/handleError';
import { useLoaderStore } from '@/stores/loader';
import { showConfirmDialog, showDialog, showToast } from '@/utils/swal';
import profileIcon from '@/assets/images/profile.svg'
import noDataImg from '@/assets/images/no-data-img.png'
import { useAuthStore } from '@/stores/auth';

const route = useRoute();
const router = useRouter();
const loader = useLoaderStore();

const auth = useAuthStore();

const page = ref(1);
const totalPages = ref(1);
const totalItems = ref(0);

const organizationId = Number(route.params.organization ?? 0);
const organization = ref(null);
const ownerData = ref(null);
const administrators = ref([]);

const selectedUsers = ref([]);
const showModal = ref(false);

const filter = ref({
    search: ''
});

onMounted(async () => {
    init();
});

const init = () => {
    selectedUsers.value = [];
    filter.value.search = '';
    page.value = 1;

    showModal.value = false;
    fetchData();
}

const fetchData = async (search = filter.value.search) => {
    try {
        loader.show();

        const { data } = await axios.get(`/organizations/${organizationId}/administrators`, {
            params: {
                search,
                page: page.value,
                per_page: 10
            }
        });

        organization.value = data.data.organization;
        ownerData.value = organization.value.user ?? null;
        administrators.value = data.data.administrators.data;

        // Optional: store pagination meta if needed
        totalPages.value = data.data.administrators.last_page;
        totalItems.value = data.data.administrators.total;

        filter.value.search = search;
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};


watch(page, () => {
    fetchData();
});

// eslint-disable-next-line
const handleSearch = debounce(function () {
    page.value = 1;
    fetchData(filter.value.search);
}, 400);

const deleteSelected = async () => {
    if (!selectedUsers.value.length) {
        showDialog({
            icon: 'warning',
            title: 'No Selection',
            html: 'Please select at least one learner to delete.'
        });

        return;
    }

    const result = await showConfirmDialog({
        title: 'Are you sure?',
        html: '<p id="text-message">Are you sure you want to delete the selected Administrators?</p><span class="rlms-text-danger-500" id="delete-confirm"> This action will permanently remove and cannot be undone. </span>',
        imageUrl: confirmationIcon,
        confirmButtonText: 'Delete',
    });

    if (result.isConfirmed) {
        try {
            loader.show();
            await axios.delete(`/organizations/${organizationId}/administrators/1`, {
                data: {
                    users: selectedUsers.value
                }
            });

            showToast({
                icon: 'success',
                title: 'Deleted! ',
                text: 'The selected administrators have been deleted successfully!',
            });

            const administrator = administrators.value.find(i => i.administrator_id === auth?.userinfo?.id);
            
            if(administrator?.id && selectedUsers.value.includes(administrator.id)) {
                router.push(`/organizations`);
                return;
            } else {
                init();
            }
        } catch (error) {
            handleError(error);
        } finally {
            loader.hide();
        }
    }
};
</script>